import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TablePagination,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Replay as ReplayIcon,
  Timer as TimerIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip } from 'recharts';
import apiClient from '../../api/client';
import { theme } from '../../styles/theme';

interface QueueStatus {
  total_messages: number;
  by_status: {
    [key: string]: number;
  };
  oldest_message_age_seconds: number;
  newest_message_age_seconds: number;
}

interface QueueMessage {
  request_id: string;
  country: string;
  metric: string;
  year: number;
  status: string;
  created_at: string;
  time_in_queue_seconds: number;
}

interface MessageDetails {
  request: any;
  result: any;
  retry_history: any[];
}

const QueueMonitor: React.FC = () => {
  const [queueStatus, setQueueStatus] = useState<QueueStatus | null>(null);
  const [messages, setMessages] = useState<QueueMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<MessageDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchQueueStatus = useCallback(async () => {
    try {
      const response = await apiClient.get('/collection/queue/status');
      setQueueStatus(response);
    } catch (error) {
      console.error('Failed to fetch queue status:', error);
    }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const params: any = {
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await apiClient.get('/collection/queue/messages', { params });
      setMessages(response.messages || []);
    } catch (error) {
      console.error('Failed to fetch queue messages:', error);
    }
  }, [statusFilter, page, rowsPerPage]);

  const fetchMessageDetails = async (requestId: string) => {
    try {
      const response = await apiClient.get(`/collection/queue/messages/${requestId}`);
      setSelectedMessage(response);
      setDetailsOpen(true);
    } catch (error) {
      console.error('Failed to fetch message details:', error);
    }
  };

  const handleRetry = async (requestIds: string[]) => {
    try {
      await apiClient.post('/collection/queue/retry', {
        request_ids: requestIds,
      });
      // Refresh data after retry
      await fetchQueueStatus();
      await fetchMessages();
    } catch (error) {
      console.error('Failed to retry messages:', error);
    }
  };

  const handleDelete = async (requestIds: string[]) => {
    try {
      await apiClient.delete('/collection/requests', {
        data: { request_ids: requestIds }
      });
      // Refresh data after deletion
      await fetchQueueStatus();
      await fetchMessages();
    } catch (error) {
      console.error('Failed to delete requests:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([fetchQueueStatus(), fetchMessages()]);
    setRefreshing(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchQueueStatus(), fetchMessages()]);
      setLoading(false);
    };
    fetchData();
  }, [fetchQueueStatus, fetchMessages]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchQueueStatus();
      fetchMessages();
    }, 5000);

    return () => clearInterval(interval);
  }, [autoRefresh, fetchQueueStatus, fetchMessages]);

  const getStatusColor = (status: string): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'warning';
      case 'processing':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'retrying':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  const pieChartData = queueStatus
    ? Object.entries(queueStatus.by_status).map(([status, count]) => ({
        name: status,
        value: count,
      }))
    : [];

  const COLORS = {
    pending: theme.colors.status.warning,
    processing: theme.colors.status.info,
    completed: theme.colors.status.success,
    failed: theme.colors.status.error,
    retrying: theme.colors.secondary.main,
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
        <CircularProgress sx={{ color: theme.colors.primary.main }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontFamily: theme.fonts.primary,
            color: theme.colors.text.primary,
            fontWeight: 400
          }}
        >
          Queue Monitor
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{
            minWidth: 120,
            '& .MuiInputLabel-root': {
              fontFamily: theme.fonts.primary,
            },
            '& .MuiOutlinedInput-root': {
              fontFamily: theme.fonts.primary,
              '& fieldset': {
                borderColor: theme.colors.border.light,
              },
              '&:hover fieldset': {
                borderColor: theme.colors.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary.main,
              },
            },
          }}>
            <InputLabel>Auto Refresh</InputLabel>
            <Select
              value={autoRefresh ? 'on' : 'off'}
              label="Auto Refresh"
              onChange={(e) => setAutoRefresh(e.target.value === 'on')}
            >
              <MenuItem value="on">On</MenuItem>
              <MenuItem value="off">Off</MenuItem>
            </Select>
          </FormControl>
          <IconButton
            onClick={handleRefresh}
            disabled={refreshing}
            sx={{
              color: theme.colors.primary.main,
              '&:hover': {
                backgroundColor: 'rgba(250, 175, 144, 0.1)',
              },
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {refreshing && <LinearProgress sx={{ mb: 2, '& .MuiLinearProgress-bar': { backgroundColor: theme.colors.primary.main } }} />}

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Card sx={{
                borderRadius: theme.widget.borderRadius,
                boxShadow: theme.widget.boxShadow,
                '&:hover': {
                  boxShadow: theme.widget.hoverBoxShadow,
                },
              }}>
                <CardContent>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="caption"
                    sx={{ fontFamily: theme.fonts.primary }}
                  >
                    Total Messages
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.text.primary,
                      fontWeight: 500
                    }}
                  >
                    {queueStatus?.total_messages || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{
                borderRadius: theme.widget.borderRadius,
                boxShadow: theme.widget.boxShadow,
                '&:hover': {
                  boxShadow: theme.widget.hoverBoxShadow,
                },
              }}>
                <CardContent>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="caption"
                    sx={{ fontFamily: theme.fonts.primary }}
                  >
                    Pending
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.status.warning,
                      fontWeight: 500
                    }}
                  >
                    {queueStatus?.by_status.pending || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{
                borderRadius: theme.widget.borderRadius,
                boxShadow: theme.widget.boxShadow,
                '&:hover': {
                  boxShadow: theme.widget.hoverBoxShadow,
                },
              }}>
                <CardContent>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="caption"
                    sx={{ fontFamily: theme.fonts.primary }}
                  >
                    Processing
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.status.info,
                      fontWeight: 500
                    }}
                  >
                    {queueStatus?.by_status.processing || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card sx={{
                borderRadius: theme.widget.borderRadius,
                boxShadow: theme.widget.boxShadow,
                '&:hover': {
                  boxShadow: theme.widget.hoverBoxShadow,
                },
              }}>
                <CardContent>
                  <Typography
                    color="text.secondary"
                    gutterBottom
                    variant="caption"
                    sx={{ fontFamily: theme.fonts.primary }}
                  >
                    Failed
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.status.error,
                      fontWeight: 500
                    }}
                  >
                    {queueStatus?.by_status.failed || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{
            height: '100%',
            borderRadius: theme.widget.borderRadius,
            boxShadow: theme.widget.boxShadow,
            '&:hover': {
              boxShadow: theme.widget.hoverBoxShadow,
            },
          }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  fontFamily: theme.fonts.primary,
                  color: theme.colors.text.primary,
                  fontWeight: 500
                }}
              >
                Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={150}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={60}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[entry.name as keyof typeof COLORS] || '#888'} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{
        mb: 2,
        borderRadius: theme.widget.borderRadius,
        boxShadow: theme.widget.boxShadow,
      }}>
        <Box sx={{ p: 2 }}>
          <FormControl size="small" sx={{
            minWidth: 150,
            '& .MuiInputLabel-root': {
              fontFamily: theme.fonts.primary,
            },
            '& .MuiOutlinedInput-root': {
              fontFamily: theme.fonts.primary,
              '& fieldset': {
                borderColor: theme.colors.border.light,
              },
              '&:hover fieldset': {
                borderColor: theme.colors.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.colors.primary.main,
              },
            },
          }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="processing">Processing</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
              <MenuItem value="retrying">Retrying</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Request ID</TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Country</TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Metric</TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Year</TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Status</TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Time in Queue</TableCell>
                <TableCell sx={{ fontFamily: theme.fonts.primary, fontWeight: 500 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {messages.map((message) => (
                <TableRow key={message.request_id}>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: theme.fonts.mono }}>
                      {message.request_id.substring(0, 12)}...
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontFamily: theme.fonts.primary }}>{message.country}</TableCell>
                  <TableCell sx={{ fontFamily: theme.fonts.primary }}>{message.metric}</TableCell>
                  <TableCell sx={{ fontFamily: theme.fonts.primary }}>{message.year}</TableCell>
                  <TableCell>
                    <Chip
                      label={message.status}
                      color={getStatusColor(message.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimerIcon fontSize="small" sx={{ color: theme.colors.text.secondary }} />
                      <Typography variant="body2" sx={{ fontFamily: theme.fonts.primary }}>
                        {formatDuration(message.time_in_queue_seconds)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => fetchMessageDetails(message.request_id)}
                          sx={{
                            color: theme.colors.secondary.main,
                            '&:hover': {
                              backgroundColor: 'rgba(107, 155, 209, 0.1)',
                            },
                          }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                      {message.status === 'failed' && (
                        <Tooltip title="Retry">
                          <IconButton
                            size="small"
                            onClick={() => handleRetry([message.request_id])}
                            sx={{
                              color: theme.colors.status.warning,
                            '&:hover': {
                              backgroundColor: 'rgba(245, 166, 35, 0.1)',
                            },
                          }}
                        >
                          <ReplayIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {message.status === 'pending' && (
                      <Tooltip title="Delete Request">
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (window.confirm(`Delete pending request ${message.request_id}?`)) {
                              handleDelete([message.request_id]);
                            }
                          }}
                          sx={{
                            color: theme.colors.status.error,
                            '&:hover': {
                              backgroundColor: 'rgba(235, 74, 95, 0.1)',
                            },
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {messages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography
                      sx={{
                        color: theme.colors.text.secondary,
                        fontFamily: theme.fonts.primary
                      }}
                    >
                      No messages in queue
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={queueStatus?.total_messages || 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
        />
      </Paper>

      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: theme.widget.borderRadius,
          }
        }}
      >
        <DialogTitle sx={{ fontFamily: theme.fonts.primary }}>Message Details</DialogTitle>
        <DialogContent>
          {selectedMessage && (
            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{
                  fontFamily: theme.fonts.primary,
                  color: theme.colors.text.primary,
                  fontWeight: 500
                }}
              >
                Request Information
              </Typography>
              <pre style={{ overflow: 'auto', fontSize: '0.875rem' }}>
                {JSON.stringify(selectedMessage.request, null, 2)}
              </pre>

              {selectedMessage.result && (
                <>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      mt: 2,
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.text.primary,
                      fontWeight: 500
                    }}
                  >
                    Result
                  </Typography>
                  <pre style={{ overflow: 'auto', fontSize: '0.875rem' }}>
                    {JSON.stringify(selectedMessage.result, null, 2)}
                  </pre>
                </>
              )}

              {selectedMessage.retry_history && selectedMessage.retry_history.length > 0 && (
                <>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{
                      mt: 2,
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.text.primary,
                      fontWeight: 500
                    }}
                  >
                    Retry History
                  </Typography>
                  <pre style={{ overflow: 'auto', fontSize: '0.875rem' }}>
                    {JSON.stringify(selectedMessage.retry_history, null, 2)}
                  </pre>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsOpen(false)}
            sx={{
              fontFamily: theme.fonts.primary,
              color: theme.colors.primary.main,
              '&:hover': {
                backgroundColor: 'rgba(250, 175, 144, 0.05)',
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QueueMonitor;